import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon, ModalDialog, Mtds, useErrorToast } from 'prove-common'
import Form, { type IChangeEvent } from '@rjsf/core'
import {
  ConfirmResultFormData,
  confirmResultSchema
} from '../../schemas/confirm-result-schema'
import { confirmResultUiSchema } from '../../schemas/confirm-result-ui-schema'
import { templates, widgets } from '../ui-schema-components'
import { useUpdateResultMutation } from '../../hooks/useResult'
import { customizeValidator } from '@rjsf/validator-ajv8'
import type { ResultSet } from '../../types/types'
import { useState } from 'react'
import { useGetSampleByIdQuery } from '../../hooks/useSample'
import { useAuth } from '../../hooks/useAuth'
import { styles } from '@mattilsynet/design'

type ConfirmResultModalProps = {
  sampleId: string
  requisitionId: number
  resultId: number
  resultStatus: ResultSet['status']
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
}

export const ConfirmResultModal = ({
  sampleId,
  requisitionId,
  resultId,
  resultStatus,
  isOpen,
  setOpen
}: ConfirmResultModalProps) => {
  const queryClient = useQueryClient()
  const updateResult = useUpdateResultMutation()
  const showError = useErrorToast('bekrefte resultatet')
  const { data: sample } = useGetSampleByIdQuery(sampleId)
  const { isAdmin, userId } = useAuth()
  const canReset =
    sample?.state === 'ReadyToProcess' &&
    (isAdmin === true || sample.userId === `user:${userId}`)
  const schema = canReset
    ? confirmResultSchema
    : {
        ...confirmResultSchema,
        properties: {
          ...confirmResultSchema.properties,
          conclusion: {
            ...(confirmResultSchema.properties?.conclusion as object),
            oneOf: [
              { const: 'NEGATIVE', title: 'Ikke påvist' },
              { const: 'POSITIVE', title: 'Påvist' }
            ]
          }
        }
      }
  const validator = customizeValidator<ConfirmResultFormData>()
  const isEditing = resultStatus === 'POSITIVE' || resultStatus === 'NEGATIVE'
  const initialFormData = isEditing
    ? ({ conclusion: resultStatus } satisfies ConfirmResultFormData)
    : ({ conclusion: 'UNDETERMINED' } satisfies ConfirmResultFormData)
  const [formData, setFormData] = useState<ConfirmResultFormData | undefined>(
    initialFormData
  )
  const closeModal = () => {
    setFormData(initialFormData)
    setOpen(false)
  }
  const onSubmit = async ({
    formData
  }: IChangeEvent<ConfirmResultFormData>): Promise<void> => {
    if (
      !formData ||
      !sample?.etag ||
      updateResult.isPending ||
      formData.conclusion === resultStatus
    )
      return
    if (formData.conclusion === 'UNDETERMINED' && !canReset) return
    try {
      await updateResult.mutateAsync({
        requisitionId,
        resultId,
        etag: sample.etag,
        body: {
          status: {
            status: formData.conclusion
          }
        }
      })
      await queryClient.invalidateQueries({
        queryKey: ['samples']
      })
      await queryClient.invalidateQueries({ queryKey: ['requisitions'] })
      setFormData(formData)
      setOpen(false)
    } catch (error) {
      showError(error)
    }
  }
  return (
    <ModalDialog
      closedby={updateResult.isPending ? 'none' : 'any'}
      open={isOpen}
      onClose={(e) => {
        e.stopPropagation()
        if (!updateResult.isPending) closeModal()
      }}
    >
      <Mtds.Grid data-gap='4'>
        <Mtds.Heading>
          {isEditing ? 'Rediger resultat' : 'Vurder resultat'}
        </Mtds.Heading>
        <Form<ConfirmResultFormData>
          className={styles.grid}
          data-gap='4'
          noHtml5Validate
          schema={schema}
          disabled={updateResult.isPending}
          uiSchema={confirmResultUiSchema}
          widgets={widgets}
          templates={templates}
          showErrorList={false}
          validator={validator}
          formData={formData}
          onChange={({ formData }) => setFormData(formData)}
          onSubmit={onSubmit}
          experimental_defaultFormStateBehavior={{
            constAsDefaults: 'skipOneOf'
          }}
        >
          {isEditing && formData?.conclusion === 'UNDETERMINED' && (
            <Mtds.Alert data-color='warning'>
              Vurderingen nullstilles. Analyseresultatene beholdes, og
              resultatet må vurderes på nytt før prøven kan ferdigstilles.
            </Mtds.Alert>
          )}
          <Mtds.Flex data-gap='3'>
            <Mtds.Button
              type='button'
              onClick={closeModal}
              disabled={updateResult.isPending}
            >
              Lukk
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='submit'
              aria-busy={updateResult.isPending}
              disabled={
                updateResult.isPending ||
                !sample?.etag ||
                !formData ||
                formData.conclusion === resultStatus
              }
            >
              <CheckIcon />
              Bekreft
            </Mtds.Button>
          </Mtds.Flex>
        </Form>
      </Mtds.Grid>
    </ModalDialog>
  )
}
